// Drift Team Website 2025
// src/contexts/AchievementsContext.jsx
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 23.04.2025 to prevent duplicate requests and enhance logging

import { createContext, useState, useEffect, useContext, useRef } from 'react';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';

export const AchievementsContext = createContext();

export const AchievementsProvider = ({ children }) => {
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const language = getUserLanguage();
  const isFetching = useRef(false); // Pojistka proti duplicitním požadavkům

  useEffect(() => {
    const loadAchievements = async () => {
      if (isFetching.current) {
        console.log('[AchievementsContext] Fetch already in progress, skipping');
        return;
      }

      isFetching.current = true;
      const cacheKey = `achievement_stats_${language}`;
      const cacheExpiryKey = `${cacheKey}_expiry`;
      let cachedStats = null;

      try {
        cachedStats = localStorage.getItem(cacheKey);
        const cacheExpiry = localStorage.getItem(cacheExpiryKey);

        // Kontrola cache (expirace 30 minut)
        if (cachedStats && cacheExpiry && Date.now() < parseInt(cacheExpiry)) {
          console.log('[AchievementsContext] Using cached achievement stats:', { cacheKey });
          setStats(JSON.parse(cachedStats));
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('[AchievementsContext] Error reading cache:', err.message);
      }

      try {
        console.log('[AchievementsContext] Fetching achievement stats from API:', { language });
        const headers = { 'accept-language': language };
        const [achievementsData, statsData] = await Promise.all([
          get('/achievements', headers),
          get('/achievement-stats', headers),
        ]);

        console.log('[AchievementsContext] API response received:', {
          achievementsCount: achievementsData?.length,
          statsCount: statsData?.length,
        });

        // Uložení do cache
        try {
          localStorage.setItem(cacheKey, JSON.stringify(statsData));
          localStorage.setItem(cacheExpiryKey, Date.now() + 30 * 60 * 1000); // 30 minut expirace
          console.log('[AchievementsContext] Cached achievement stats:', { cacheKey });
        } catch (err) {
          console.error('[AchievementsContext] Error saving to cache:', err.message);
        }

        setAchievements(Array.isArray(achievementsData) ? achievementsData : []);
        setStats(Array.isArray(statsData) ? statsData : []);
        setError(null);
      } catch (err) {
        console.error('[AchievementsContext] Error fetching achievement stats:', err.message);
        setError(err.message || 'Failed to load achievement stats');
        setAchievements([]);
        setStats([]);
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    };

    loadAchievements();
  }, [language]);

  // Přidání funkce updateStats, která chybí a způsobuje chybu "a is not a function"
  const updateStats = (newStats) => {
    setStats(newStats);
    
    // Aktualizace cache
    try {
      const cacheKey = `achievement_stats_${language}`;
      const cacheExpiryKey = `${cacheKey}_expiry`;
      localStorage.setItem(cacheKey, JSON.stringify(newStats));
      localStorage.setItem(cacheExpiryKey, Date.now() + 30 * 60 * 1000);
      console.log('[AchievementsContext] Updated cached achievement stats:', { cacheKey });
    } catch (err) {
      console.error('[AchievementsContext] Error updating cache:', err.message);
    }
  };

  return (
    <AchievementsContext.Provider value={{ 
      achievements, 
      stats, 
      loading, 
      error,
      updateStats // Přidání funkce updateStats do kontextu
    }}>
      {children}
    </AchievementsContext.Provider>
  );
};

export const useAchievements = () => {
  const context = useContext(AchievementsContext);
  if (!context) {
    throw new Error('useAchievements must be used within an AchievementsProvider');
  }
  return context;
};